import { createSelector } from '@reduxjs/toolkit';

import { ComponentDesign, LayoutModel } from '@paragon/contract-library';
import { getLayoutModelsWithPendingChanges } from '@paragon/layout-models';

import { isConsideredDesignItem } from '../../library/selection/types';
import { RootState } from '..';
import { getProjectLayoutModel } from './getProjectLayoutModel';
import { getSingleSelectedItemEntryOrNull } from './getSelectedItemSelector';
import { getComponentDesignBeingInserted } from './insertionObjectSelectors';
import { isInsertingComponentDesign } from './isInsertingComponentDesignSelector';

const getSelectedItemType = (state: RootState) => getSingleSelectedItemEntryOrNull(state)?.type;
const getSelectedItemGuid = (state: RootState) => getSingleSelectedItemEntryOrNull(state)?.item.guid;
const getComponentDesigns = (state: RootState) => state.componentDesigns.data;

interface ComponentSelectedModel {
  type: 'component';
  insertion: boolean;
  model: ComponentDesign;
}

interface LayoutModelSelectModel {
  type: 'layout-model';
  model: LayoutModel;
}

export type SettingsPanelSelectedModel = ComponentSelectedModel | LayoutModelSelectModel;

export function isComponentModel(model: SettingsPanelSelectedModel): model is ComponentSelectedModel {
  return model != null && model.type === 'component';
}

export function isInsertionComponentModel(model: SettingsPanelSelectedModel): model is ComponentSelectedModel {
  return isComponentModel(model) && model.insertion;
}

export function isSettingsPanelLayoutModel(model: SettingsPanelSelectedModel): model is LayoutModelSelectModel {
  return model != null && model.type === 'layout-model';
}

export const getSelectedModelForSettingsPanel = createSelector(
  isInsertingComponentDesign,
  getComponentDesignBeingInserted,
  getSelectedItemGuid,
  getComponentDesigns,
  getLayoutModelsWithPendingChanges,

  (
    isInserting,
    insertionComponentDesign,
    selectedItemGuid,
    componentDesigns,
    layoutModels,
  ): SettingsPanelSelectedModel | null => {
    if (selectedItemGuid == null) return null;

    if (isInserting) {
      if (insertionComponentDesign) {
        return { type: 'component' as const, insertion: true, model: insertionComponentDesign };
      }

      return null;
    }

    const componentDesign = componentDesigns[selectedItemGuid];
    if (componentDesign) {
      return { type: 'component' as const, insertion: false, model: componentDesign };
    }

    return { type: 'layout-model' as const, model: layoutModels[selectedItemGuid] };
  },
);

export const getComponentTypeForSettingsPanel = createSelector(getSelectedModelForSettingsPanel, (selectedModel) => {
  if (selectedModel == null) return null;

  return isComponentModel(selectedModel) ? selectedModel.model.component.componentType : ('NonComponent' as const);
});

export const settingsPanelIsInProjectContext = createSelector(
  getSelectedItemType,
  getProjectLayoutModel,
  (selectedItemType, projectLayoutModel) =>
    selectedItemType && !isConsideredDesignItem(selectedItemType) && projectLayoutModel != null,
);
